import { loadStripe } from '@stripe/stripe-js';

// Define the base URL for your backend API
const apiUrl = process.env.REACT_APP_API_URL || 'http://146.190.43.152:4242';

let stripePromise;
const getStripe = () => {
  if (!stripePromise) {
    // Fallback key is a test key. Replace with your actual publishable key in .env
    const publishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || 'pk_test_51Hh9DRLfnK8aR3k8YJ1fWchbEW3g1x5bYnO8Z8Y8d8g8e8f8g8h8i8j8k8l8m8n8o8p8q';
    stripePromise = loadStripe(publishableKey);
  }
  return stripePromise;
};

// Create a Stripe Checkout Session by calling the backend
export const createCheckoutSession = async (orderData) => {
  const stripe = await getStripe();

  try {
    const response = await fetch(`${apiUrl}/create-checkout-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(orderData),
    });

    if (!response.ok) {
      throw new Error(`Server error: ${response.statusText}`);
    }

    const session = await response.json();

    // Redirect to Stripe Checkout
    const { error } = await stripe.redirectToCheckout({
      sessionId: session.sessionId,
    });

    if (error) {
      console.error('Stripe redirect error:', error);
      // Let the caller handle the UI update for the error
      throw error;
    }
  } catch (error) {
    console.error('Error creating checkout session:', error);
    // Re-throw the error so the calling component can handle it
    throw error;
  }
};

// Service for creating payment intents
export const createPaymentIntent = async (orderData) => {
  try {
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: orderData.total,
        currency: 'mxn',
        metadata: {
          orderId: orderData.orderId,
          customerEmail: orderData.customerEmail,
          items: JSON.stringify(orderData.items.map(item => ({
            id: item.id,
            name: item.name,
            quantity: item.quantity,
            price: item.price
          })))
        }
      }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating payment intent:', error);
    throw error;
  }
};

// Service for confirming payment
export const confirmPayment = async (stripe, clientSecret, paymentData) => {
  try {
    const result = await stripe.confirmPayment({
      clientSecret,
      confirmParams: {
        return_url: window.location.origin + '/order-confirmation',
        payment_method_data: {
          billing_details: {
            name: paymentData.customerName,
            email: paymentData.customerEmail,
            phone: paymentData.customerPhone,
            address: {
              line1: paymentData.address?.line1,
              city: paymentData.address?.city,
              state: paymentData.address?.state,
              postal_code: paymentData.address?.postalCode,
              country: 'MX'
            }
          }
        }
      }
    });

    if (result.error) {
      throw new Error(result.error.message);
    }

    return result;
  } catch (error) {
    console.error('Error confirming payment:', error);
    throw error;
  }
};

// Service for handling OXXO payments
export const createOxxoPayment = async (orderData) => {
  try {
    const response = await fetch('/api/create-oxxo-payment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        amount: orderData.total,
        currency: 'mxn',
        customer_email: orderData.customerEmail,
        metadata: {
          orderId: orderData.orderId,
          customerName: orderData.customerName,
          items: JSON.stringify(orderData.items)
        }
      }),
    });

    if (!response.ok) {
      throw new Error('Network response was not ok');
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error creating OXXO payment:', error);
    throw error;
  }
};

// Utility function to format currency
export const formatCurrency = (amount, currency = 'MXN') => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: currency,
  }).format(amount);
};

// Utility function to calculate Stripe fees
export const calculateStripeFees = (amount, paymentMethod = 'card') => {
  const feeRates = {
    card: { percentage: 0.036, fixed: 3 }, // 3.6% + $3 MXN
    oxxo: { percentage: 0.036, fixed: 11 }, // 3.6% + $3 MXN + $8 MXN
    spei: { percentage: 0.009, fixed: 3 }, // 0.9% + $3 MXN
  };

  const rate = feeRates[paymentMethod] || feeRates.card;
  const percentageFee = amount * rate.percentage;
  const totalFee = percentageFee + rate.fixed;
  
  return {
    percentageFee: Math.round(percentageFee * 100) / 100,
    fixedFee: rate.fixed,
    totalFee: Math.round(totalFee * 100) / 100,
    netAmount: Math.round((amount - totalFee) * 100) / 100
  };
};

// Error handling utility
export const handleStripeError = (error) => {
  const errorMessages = {
    'card_declined': 'Tu tarjeta fue rechazada. Por favor, intenta con otra tarjeta.',
    'insufficient_funds': 'Fondos insuficientes. Por favor, verifica tu saldo.',
    'expired_card': 'Tu tarjeta ha expirado. Por favor, utiliza una tarjeta válida.',
    'incorrect_cvc': 'El código de seguridad es incorrecto.',
    'processing_error': 'Ocurrió un error al procesar el pago. Por favor, intenta de nuevo.',
    'rate_limit': 'Demasiados intentos. Por favor, espera un momento e intenta de nuevo.',
  };

  return errorMessages[error.code] || error.message || 'Ocurrió un error inesperado. Por favor, intenta de nuevo.';
};

export { getStripe }; 